import React from 'react';
import {
  Badge,
  Header,
  StatusIndicator,
  Table,
} from '@amzn/awsui-components-console';
import { useTranslation } from 'react-i18next';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { rollbackSingleQuote } from 'src/utils/helpers';
import { LinkedDevice } from 'src/API';
import { useAppSelector } from 'src/stores/slices/hooks';
import { testIds } from 'src/features/isc/constants';

const DeviceList = ({
  devices,
  selectedDevices,
  selectionChangeHandler,
  selectionDisabled = false,
}: {
  devices: LinkedDevice[];
  selectedDevices: LinkedDevice[];
  selectionChangeHandler: (deviceList: LinkedDevice[]) => void;
  selectionDisabled: boolean;
}) => {
  const { t } = useTranslation();
  const unlinkingDevicesStatus = useAppSelector(
    state => state.deviceState.unlinkDevices
  );

  const { items, collectionProps } = useCollection(devices, {});

  return (
    <div
      style={{
        overflow: 'auto',
        width: '100%',
        height: '70vh',
        padding: '20px 10px',
      }}>
      <Table
        data-testid={testIds.UNLINK_MODAL_TABLE}
        {...collectionProps}
        columnDefinitions={[
          {
            id: 'deviceName',
            header: t('Device Name'),
            cell: (item): string => item.deviceName || '-',
          },
          {
            id: 'cameraName',
            header: t('Camera Name'),
            cell: (item): string => item.cameraName || '-',
          },
          {
            id: 'status',
            header: t('Status'),
            cell: (item): React.ReactElement => {
              const { done, errorText, loading } =
                unlinkingDevicesStatus[item.deviceName] || {};
              if (errorText) {
                // If single quote has been changed to double single quote, rollback it
                return (
                  <Badge color="red">
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {t(rollbackSingleQuote(errorText))}
                    </span>
                  </Badge>
                );
              }
              if (done) {
                return (
                  <StatusIndicator type="success">
                    {t('Success')}
                  </StatusIndicator>
                );
              }
              if (loading) {
                return (
                  <StatusIndicator type="loading">
                    {t('Working')}
                  </StatusIndicator>
                );
              }
              return <StatusIndicator type="stopped"></StatusIndicator>;
            },
            sortingField: 'status',
          },
        ]}
        items={items}
        sortingDisabled
        empty={<></>}
        header={<Header>Linked Devices</Header>}
        selectionType="multi"
        selectedItems={selectedDevices}
        isItemDisabled={() => selectionDisabled}
        onSelectionChange={({ detail }) => {
          selectionChangeHandler([...detail.selectedItems]);
        }}
      />
    </div>
  );
};

export default DeviceList;
